/**
 * @fileoverview Route guard reproducing the Angular `authGuard`.
 * @module app/guards/RequireAuth
 * @description
 * React port of `angular/src/app/guards/auth.guard.ts` (`authGuard`). Renders the
 * protected route (`<Outlet/>`) when access is allowed, otherwise redirects via
 * `<Navigate replace/>`. Reproduces the Angular decision logic exactly:
 *
 *   1. While `data.isLoading` (session init), wait up to 3s. If still loading
 *      after the timeout, block access -> `/login?returnUrl=<path>`.
 *   2. If not `isLoggedIn` -> `/login?returnUrl=<path>`.
 *   3. While `isLoadingProfile`, wait up to 3s (then proceed regardless, matching
 *      Angular which logged a timeout but continued).
 *   4. If `canAccessRoute(currentRoutePath)` -> render children, else
 *      -> `/<getNextStep()>`.
 *
 * The `currentRoutePath` matches what Angular passed (`route.routeConfig.path`,
 * e.g. 'dashboard', 'role-selection') and is supplied explicitly by the router.
 *
 * @author Liraz Amir
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useDataStore } from '@/stores/data.store';
import { useAuthStore } from '@/stores/auth.store';
import { GuardLoading } from './GuardLoading';

/** Max time to wait for an async condition before proceeding (matches Angular). */
const WAIT_TIMEOUT_MS = 3000;

interface RequireAuthProps {
  /** The route path segment Angular passed to the guard (e.g. 'dashboard'). */
  routePath: string;
}

/** Phases of the guard's wait/decision lifecycle. */
type Phase = 'init-wait' | 'profile-wait' | 'decided';

export function RequireAuth({ routePath }: RequireAuthProps) {
  const location = useLocation();
  const dataLoading = useDataStore((s) => s.isLoading);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const isLoadingProfile = useAuthStore((s) => s.isLoadingProfile);

  // Whether the session-init wait timed out while still loading (=> block).
  const [initTimedOut, setInitTimedOut] = useState(false);
  // Whether the profile-load wait has finished (timeout or completion).
  const [profileWaitDone, setProfileWaitDone] = useState(false);
  const initTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const profileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Step 1: wait up to 3s for session init; if still loading, mark timed out.
  useEffect(() => {
    if (!dataLoading) {
      setInitTimedOut(false);
      if (initTimerRef.current) {
        clearTimeout(initTimerRef.current);
        initTimerRef.current = null;
      }
      return;
    }
    if (initTimerRef.current) return;
    initTimerRef.current = setTimeout(() => {
      // Re-read current state: only block if STILL loading after the timeout.
      if (useDataStore.getState().isLoading) {
        setInitTimedOut(true);
      }
    }, WAIT_TIMEOUT_MS);
    return () => {
      if (initTimerRef.current) {
        clearTimeout(initTimerRef.current);
        initTimerRef.current = null;
      }
    };
  }, [dataLoading]);

  // Step 3: once logged in, wait up to 3s for profile load, then proceed.
  useEffect(() => {
    if (!isLoadingProfile) {
      setProfileWaitDone(true);
      if (profileTimerRef.current) {
        clearTimeout(profileTimerRef.current);
        profileTimerRef.current = null;
      }
      return;
    }
    setProfileWaitDone(false);
    if (profileTimerRef.current) return;
    profileTimerRef.current = setTimeout(() => {
      // Angular logged a timeout but proceeded regardless.
      setProfileWaitDone(true);
    }, WAIT_TIMEOUT_MS);
    return () => {
      if (profileTimerRef.current) {
        clearTimeout(profileTimerRef.current);
        profileTimerRef.current = null;
      }
    };
  }, [isLoadingProfile]);

  const returnUrl = location.pathname + location.search;
  const loginRedirect = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;

  // Phase resolution (mirrors the sequential guard flow).
  let phase: Phase;
  if (dataLoading && !initTimedOut) {
    phase = 'init-wait';
  } else if (!initTimedOut && isLoggedIn && (isLoadingProfile || !profileWaitDone)) {
    phase = 'profile-wait';
  } else {
    phase = 'decided';
  }

  if (phase === 'init-wait' || phase === 'profile-wait') {
    return <GuardLoading />;
  }

  // Step 1 (timeout) -> block to login.
  if (initTimedOut) {
    return <Navigate to={loginRedirect} replace />;
  }

  // Step 2: not authenticated -> login with returnUrl.
  if (!isLoggedIn) {
    return <Navigate to={loginRedirect} replace />;
  }

  // Step 4: access decision.
  if (useAuthStore.getState().canAccessRoute(routePath)) {
    return <Outlet />;
  }

  const nextStep = useAuthStore.getState().getNextStep();
  return <Navigate to={`/${nextStep}`} replace />;
}

export default RequireAuth;
